import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { omitBy, pick } from 'lodash';
import { v4 as uuid } from 'uuid';

import { NpcAdapter } from '../adapter/npc';
import {
  postDeleteFmDraft,
  postGetFMCreateConfig,
  postGetFmDraft,
  postGetFMDraftList,
  postGetNpcProfile,
  postPreviewTone,
  postSubmitFMDraft,
  postUpdateFmDraft,
} from '../api/fm';
import { FM_TAB } from '../constants/fm';
import { AppState } from '../state';
import {
  FmTabType,
  IFMDraftItem,
  IFMDraftScript,
  IGetFMDraftListFilter,
  INpcBgmInfo,
  INpcItem,
  PreviewToneScene,
} from '../types/fm';
import { AudioManager } from '../utils/media';

export enum ActiveAudioType {
  BGM = 'bgm',
  SCRIPT = 'script',
  PREVIEW = 'preview',
}

interface IFMState {
  currentTab: FmTabType;
  [FmTabType.All]: IFMData;
  [FmTabType.Draft]: IFMData;
  sceneList: string[]; //情景主题
  tagList: string[]; //类型标签
  npcInfo: INpcItem | null;
  fmDraft: Partial<IFMDraftItem> | null;
  saveDraftTime: number | null;
  activeAudioType: ActiveAudioType;
}

interface IFMData {
  data: IFMDraftItem[];
  hasMore: boolean;
  loading: boolean;
  size: number;
  filter: IGetFMDraftListFilter;
  cursor: number;
  next_cursor: number;
}

const DefaultSize = 10;

const INIT_SCRIPT: IFMDraftScript = {
  _id: '',
  content: '',
  wait_time_ms: 1500, // 默认停顿1.5秒
  audio_url: '',
  audio_duration: 0,
};

const initialState: IFMState = {
  currentTab: FmTabType.All,
  [FmTabType.All]: {
    data: [],
    hasMore: true,
    loading: false,
    size: DefaultSize,
    filter: {
      only_not_submit: false,
    },
    cursor: 0,
    next_cursor: 0,
  },
  [FmTabType.Draft]: {
    data: [],
    hasMore: true,
    loading: false,
    size: DefaultSize,
    filter: {
      only_not_submit: true,
    },
    cursor: 0,
    next_cursor: 0,
  },
  sceneList: [],
  tagList: [],
  npcInfo: null,
  fmDraft: {
    last_delay: { ...INIT_SCRIPT },
  },
  saveDraftTime: null,
  activeAudioType: ActiveAudioType.PREVIEW,
};

export const getFmLibraryList = createAsyncThunk(
  'fm/postGetFMDraftList',
  async (_, { getState, rejectWithValue }) => {
    try {
      const {
        user: { authData },
        fm,
      } = getState() as AppState;

      const { currentTab } = fm;
      const { size, filter, next_cursor } = fm[currentTab];

      const res = await postGetFMDraftList(
        { cursor: next_cursor, size, filter },
        { params: { authData } }
      );
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getFmConfig = createAsyncThunk(
  'fm/getFmCreateConfig',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authData } = (getState() as AppState).user;
      const res = await postGetFMCreateConfig(
        {},
        { params: { authData }, headers: { lane: 'fm' } }
      );
      console.log('[getFmConfig]', JSON.stringify(res));
      return res;
    } catch (err) {
      console.log('[getFmConfig err]', err);
      return rejectWithValue(err);
    }
  }
);

export const getNpcProfile = createAsyncThunk(
  'fm/getNpcProfile',
  async ({ npc_id }: { npc_id: number }, { getState, rejectWithValue }) => {
    try {
      const { authData } = (getState() as AppState).user;
      const res = await postGetNpcProfile(
        { npc_id },
        { headers: { lane: 'fm' }, params: { authData } }
      );
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getFmDraft = createAsyncThunk(
  'fm/getFmDraft',
  async ({ fm_draft_id }: { fm_draft_id: number }, { getState, rejectWithValue }) => {
    try {
      const { authData } = (getState() as AppState).user;
      return await postGetFmDraft(
        { fm_draft_id },
        { headers: { lane: 'fm' }, params: { authData } }
      );
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const generateScriptAudio = createAsyncThunk(
  'fm/generateScriptAudio',
  async ({ id }: { id: string }, { getState, rejectWithValue }) => {
    try {
      const draft = (getState() as AppState).fm.fmDraft;
      if (!draft) return;
      const paragraph = draft.script_list?.find((item) => item._id === id);
      if (!paragraph || !draft.npc_id) return;

      const second = paragraph.wait_time_ms / 1000;

      const res = await postPreviewTone({
        prompt: `${second > 0 ? `<#${second}#>` : ''}${paragraph.content}`,
        with_npc_id: draft.npc_id,
        scene: PreviewToneScene.FM,
      });
      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const concatScriptAudio = createAsyncThunk(
  'fm/concatScriptAudio',
  async (_, { getState, rejectWithValue }) => {
    const draft = (getState() as AppState).fm.fmDraft;
    if (!draft) return;
    const paragraphs = draft.script_list;
    if (!paragraphs) return;

    const audioUrls = paragraphs.filter((item) => item.audio_url);
    if (audioUrls.length === 0) {
      return;
    }
    const lastDelayMs = draft.last_delay?.wait_time_ms || 0;

    // 使用AudioContext处理音频
    const audioFiles = await Promise.all(
      audioUrls.map(async (audio, index) => {
        const blob = await fetch(audio.audio_url).then((res) => res.blob());
        return new File([blob], `audio-${index}.wav`, { type: 'audio/wav' });
      })
    );

    // 如果有末尾延迟，创建静音文件
    if (lastDelayMs > 0) {
      const silenceBlob = await AudioManager.createSilenceAudio(lastDelayMs);
      const silenceFile = new File([silenceBlob], `audio-slience.wav`, { type: 'audio/wav' });
      audioFiles.push(silenceFile);
    }

    // 合并音频
    const concatBlob = await AudioManager.concatenateAudios(audioFiles);

    // 创建新的URL
    const newUrl = URL.createObjectURL(concatBlob);
    return newUrl;
  }
);

export const requestUpdateFmDraft = createAsyncThunk(
  'fm/requestUpdateFmDraft',
  async (_, { getState, rejectWithValue }) => {
    try {
      const draft = (getState() as AppState).fm.fmDraft as IFMDraftItem;
      const { authData } = (getState() as AppState).user;
      return await postUpdateFmDraft(
        {
          fm_draft: {
            ...(pick(draft, [
              'id',
              'fm_id',
              'bg_image',
              'title',
              'npc_id',
              'script_list',
              'bgm_id',
              'npc_id',
              'tag_list',
            ]) as IFMDraftItem),
            script_list: [...(draft.script_list || []), draft.last_delay]
              .filter(Boolean)
              .map((item) => omitBy(item, (__, key) => key.startsWith('_'))) as IFMDraftScript[],
          },
        },
        { headers: { lane: 'fm' }, params: { authData } }
      );
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const submitFmDraft = createAsyncThunk(
  'fm/submitFmDraft',
  async ({ bgmVolume }: { bgmVolume: number }, { getState, rejectWithValue }) => {
    try {
      const draft = (getState() as AppState).fm.fmDraft as IFMDraftItem;
      if (!draft || !draft._concatAudioUrl) return;
      const { authData } = (getState() as AppState).user;
      return await postSubmitFMDraft(
        {
          fm_draft: {
            ...(pick(draft, [
              'id',
              'fm_id',
              'bg_image',
              'title',
              'npc_id',
              'script_list',
              'bgm_id',
              'npc_id',
              'tag_list',
            ]) as IFMDraftItem),
            bgm_info: {
              bgm_id: draft.bgm_id!,
              bgm_url: draft.bgm_info?.bgm_url!,
            } as INpcBgmInfo,
            script_list: [...(draft.script_list || []), draft.last_delay]
              .filter(Boolean)
              .map((item) => omitBy(item, (__, key) => key.startsWith('_'))) as IFMDraftScript[],
            bgm_volume: bgmVolume,
          },
        },
        { headers: { lane: 'fm' }, params: { authData } }
      );
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteFmDraft = createAsyncThunk(
  'fm/deleteFmDraft',
  async ({ fm_draft_id }: { fm_draft_id: number }, { getState, rejectWithValue }) => {
    try {
      return await postDeleteFmDraft({ fm_draft_id });
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const fmSlice = createSlice({
  name: 'fm',
  initialState,
  reducers: {
    setFmCurrentTab: (state, action: PayloadAction<FmTabType>) => {
      state.currentTab = action.payload;
    },
    updateFmDraft: (state, action: PayloadAction<Partial<IFMDraftItem>>) => {
      if (!state.fmDraft) return;
      state.fmDraft = { ...state.fmDraft, ...action.payload, _dirty: true };
    },
    insertNewFmDraftScript: (state, action: PayloadAction<{ index: number }>) => {
      if (!state.fmDraft?.script_list) return;
      const newScript = { ...INIT_SCRIPT, _id: uuid() };
      state.fmDraft?.script_list.splice(action.payload.index + 1, 0, newScript);
      if (state.fmDraft) {
        state.fmDraft._dirty = true;
      }
    },
    updateFmDraftScript: (
      state,
      action: PayloadAction<{ index: number; script: Partial<IFMDraftScript> }>
    ) => {
      if (!state.fmDraft?.script_list) return;
      state.fmDraft.script_list[action.payload.index] = {
        ...state.fmDraft.script_list[action.payload.index],
        ...action.payload.script,
        _changed: true,
      };
      if (state.fmDraft) {
        state.fmDraft._dirty = true;
      }
    },
    updateFmDraftLastDelay: (state, action: PayloadAction<{ script: Partial<IFMDraftScript> }>) => {
      if (!state.fmDraft?.last_delay) return;
      state.fmDraft.last_delay = {
        ...state.fmDraft.last_delay,
        ...action.payload.script,
        _changed: true,
      };
      if (state.fmDraft) {
        state.fmDraft._dirty = true;
      }
    },
    updateFmDraftScriptById: (
      state,
      action: PayloadAction<{ id: string; script: Partial<IFMDraftScript> }>
    ) => {
      if (!state.fmDraft?.script_list) return;
      const index = state.fmDraft.script_list.findIndex((item) => item._id === action.payload.id);
      if (index === -1) return;
      state.fmDraft.script_list[index] = {
        ...state.fmDraft.script_list[index],
        ...action.payload.script,
        _changed: true,
      };
      state.fmDraft._dirty = true;
    },
    deleteFmDraftScript: (state, action: PayloadAction<{ index: number }>) => {
      if (!state.fmDraft?.script_list) return;
      state.fmDraft.script_list.splice(action.payload.index, 1);
      if (state.fmDraft) {
        state.fmDraft._dirty = true;
      }
    },
    setConcatAudioUrl: (state, action: PayloadAction<string | undefined>) => {
      if (!state.fmDraft) return;
      state.fmDraft._concatAudioUrl = action.payload;
    },
    setActiveAudioType: (state, action: PayloadAction<ActiveAudioType>) => {
      state.activeAudioType = action.payload;
    },
    clearTabData: (state, action: PayloadAction<FmTabType>) => {
      state[action.payload] = initialState[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFmLibraryList.pending, (state, action) => {
        state[state.currentTab].loading = true;
      })
      .addCase(getFmLibraryList.fulfilled, (state, action) => {
        state[state.currentTab].data.push(...(action.payload.data_list || []));
        state[state.currentTab].hasMore = action.payload.has_more || false;
        state[state.currentTab].cursor = state[state.currentTab].next_cursor || 0;
        state[state.currentTab].next_cursor = action.payload.next_cursor || 0;
        state[state.currentTab].loading = false;
      })
      .addCase(getFmLibraryList.rejected, (state, action) => {
        state[state.currentTab].loading = false;
        state[state.currentTab].hasMore = false;
      });

    builder.addCase(getFmConfig.fulfilled, (state, action) => {
      state.sceneList = action.payload.scene_list || [];
      state.tagList = action.payload.tag_list || [];
    });

    builder.addCase(getNpcProfile.fulfilled, (state, action) => {
      console.log('getNpcProfilegetNpcProfile', action.payload);
      state.npcInfo = NpcAdapter.adaptForFm(action.payload);
    });

    builder.addCase(getFmDraft.fulfilled, (state, action) => {
      state.fmDraft = action.payload.fm_draft || null;

      let lastDelay = {
        ...INIT_SCRIPT,
      };

      let scriptList =
        state.fmDraft.script_list?.map((item) => ({
          ...item,
          _id: uuid(),
        })) || [];

      if (!scriptList[scriptList.length - 1].content) {
        lastDelay = scriptList[scriptList.length - 1];
        scriptList = scriptList.slice(0, -1);
      }

      state.fmDraft = {
        ...state.fmDraft,
        script_list: scriptList,
        last_delay: lastDelay,
      };
    });

    builder.addCase(concatScriptAudio.fulfilled, (state, action) => {
      if (!state.fmDraft) return;
      // 清理之前的URL
      if (state.fmDraft?._concatAudioUrl && state.fmDraft._concatAudioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(state.fmDraft._concatAudioUrl);
      }
      if (state.fmDraft.last_delay) {
        state.fmDraft.last_delay._changed = false;
      }
      state.fmDraft._concatAudioUrl = action.payload;
    });

    builder
      .addCase(generateScriptAudio.pending, (state, action) => {
        const paragraph = state.fmDraft?.script_list?.find(
          (item) => item._id === action.meta.arg.id
        );
        if (!paragraph) return;
        paragraph._isGenerating = true;
      })
      .addCase(generateScriptAudio.fulfilled, (state, action) => {
        const paragraph = state.fmDraft?.script_list?.find(
          (item) => item._id === action.meta.arg.id
        );
        if (!paragraph) return;
        paragraph._isGenerating = false;
        paragraph.audio_url = action.payload?.wav_url || '';
        paragraph._changed = false;
      })
      .addCase(generateScriptAudio.rejected, (state, action) => {
        const paragraph = state.fmDraft?.script_list?.find(
          (item) => item._id === action.meta.arg.id
        );
        if (!paragraph) return;
        paragraph._isGenerating = false;
        paragraph.audio_url = '';
      });

    builder
      .addCase(deleteFmDraft.pending, (state) => {
        state[state.currentTab].loading = true;
      })
      .addCase(deleteFmDraft.fulfilled, (state, action) => {
        for (const v of Object.values(FM_TAB)) {
          state[v].data = state[v].data.filter((item) => item.id !== action.meta.arg.fm_draft_id);
        }
        state[state.currentTab].loading = false;
      })
      .addCase(deleteFmDraft.rejected, (state, action) => {
        state[state.currentTab].loading = false;
      });

    builder
      .addCase(submitFmDraft.pending, (state, action) => {
        if (!state.fmDraft) return;
        state.fmDraft._isSubmitting = true;
      })
      .addCase(submitFmDraft.fulfilled, (state, action) => {
        if (!state.fmDraft) return;
        state.fmDraft._dirty = false;
        state.fmDraft._isSubmitting = false;
      })
      .addCase(submitFmDraft.rejected, (state, action) => {
        if (!state.fmDraft) return;
        state.fmDraft._isSubmitting = false;
      });

    builder
      .addCase(requestUpdateFmDraft.pending, (state, action) => {
        if (!state.fmDraft) return;
        state.fmDraft._isSubmitting = true;
      })
      .addCase(requestUpdateFmDraft.fulfilled, (state, action) => {
        if (!state.fmDraft) return;
        state.fmDraft._isSubmitting = false;
        state.fmDraft._dirty = false;
        state.saveDraftTime = Date.now();
      })
      .addCase(requestUpdateFmDraft.rejected, (state, action) => {
        if (!state.fmDraft) return;
        state.fmDraft._isSubmitting = false;
      });
  },
});

export const {
  setFmCurrentTab,
  updateFmDraft,
  insertNewFmDraftScript,
  updateFmDraftScript,
  updateFmDraftLastDelay,
  deleteFmDraftScript,
  setConcatAudioUrl,
  updateFmDraftScriptById,
  setActiveAudioType,
  clearTabData,
} = fmSlice.actions;
